import React, {useMemo} from 'react';
import {contractCRUD, legalPersonCRUD, clientCRUD} from "../../../http/CRUD";
import CRUDTableQuery from "../CRUDTableQuery";
import {formatDate} from "../../../functions/formatters";
import {getContractStates} from "../../../redux/actions/contract/contractActions";
import {checkRoles} from "../../../functions/checkRoles";
import CRUDTableStandart from "../CRUDTableStandart";
import {getFilials} from "../../../redux/actions/filialActions";

export default function ContractCRUDTable({type = 'standart', ...props}) {
    const isSuperAdmin = checkRoles("SuperAdministrator")

    // Обертка для функции поиска с поддержкой поиска по БИНу
    const customSearch = useMemo(() => {
        return async (params) => {
            const binFilter = params?.filter?.["client.legalPerson.bin"];
            
            if (binFilter && binFilter.operand1) {
                try {
                    // Находим юридических лиц по БИНу
                    const binValue = binFilter.operand1;
                    const legalPersonsResult = await legalPersonCRUD.search({
                        filter: {
                            bin: {
                                operand1: binValue,
                                operator: binFilter.operator === "like" ? "like" : "equals"
                            }
                        },
                        paging: { skip: 0, take: 1000 }
                    });

                    if (legalPersonsResult?.result?.length > 0) {
                        // Получаем ID юридических лиц
                        const legalPersonIds = legalPersonsResult.result.map(lp => lp.id);
                        
                        // Находим клиентов для каждого юридического лица
                        const clientPromises = legalPersonIds.map(legalPersonId => 
                            clientCRUD.search({
                                filter: {
                                    legalPersonId: {
                                        operand1: legalPersonId,
                                        operator: "equals"
                                    }
                                },
                                paging: { skip: 0, take: 1000 }
                            })
                        );
                        
                        const clientsResults = await Promise.all(clientPromises);
                        
                        // Собираем все ID клиентов
                        const clientIds = [];
                        clientsResults.forEach(result => {
                            if (result?.result?.length > 0) {
                                result.result.forEach(client => {
                                    if (!clientIds.includes(client.id)) {
                                        clientIds.push(client.id);
                                    }
                                });
                            }
                        });

                        if (clientIds.length > 0) {
                            // Удаляем оригинальный фильтр по БИНу
                            const {["client.legalPerson.bin"]: _, ...restFilter} = params.filter;
                            
                            // Делаем запросы для каждого clientId и объединяем результаты
                            // Так как API может не поддерживать оператор "in"
                            const contractPromises = clientIds.map(clientId =>
                                contractCRUD.search({
                                    ...params,
                                    filter: {
                                        ...restFilter,
                                        clientId: {
                                            operand1: clientId,
                                            operator: "equals"
                                        }
                                    }
                                })
                            );
                            
                            const contractResults = await Promise.all(contractPromises);
                            
                            // Объединяем все результаты
                            const allContracts = [];
                            let total = 0;
                            contractResults.forEach(result => {
                                if (result?.result) {
                                    allContracts.push(...result.result);
                                }
                                if (result?.total) {
                                    total += result.total;
                                }
                            });
                            
                            // Удаляем дубликаты по ID
                            const uniqueContracts = Array.from(
                                new Map(allContracts.map(contract => [contract.id, contract])).values()
                            );
                            
                            return {
                                result: uniqueContracts,
                                total: uniqueContracts.length
                            };
                        } else {
                            // Если клиенты не найдены, возвращаем пустой результат
                            return { result: [], total: 0 };
                        }
                    } else {
                        // Если юридические лица не найдены, возвращаем пустой результат
                        return { result: [], total: 0 };
                    }
                } catch (error) {
                    console.error("Ошибка при поиске по БИНу:", error);
                    // В случае ошибки возвращаем пустой результат
                    return { result: [], total: 0 };
                }
            }
            
            // Если поиска по БИНу нет, используем обычный поиск
            return contractCRUD.search(params);
        };
    }, []);

    const columns = [
        {title: "#", dataIndex: 'id', key: 'id'},
        {
            title: "Название",
            key: 'name',
            render: (unknown, row) => row.name ? row.name :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: "Дата окончания договора", dataIndex: 'actionAt', key: 'actionAt',
            render: (unknown, row) => row.actionAt ? formatDate(row.actionAt) :
                <span style={{color: "gray"}}>Не задана</span>
        },
        {
            title: "Клиент",
            key: 'client',
            render: (unknown, row) => row?.typeId === 5 ? <span style={{color: "gray"}}>Госзаказ</span> : row.client?.legalPersonId ? row.client?.legalPerson?.name : row.client?.personId ? row.client?.person?.fullName :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: "Тип договора",
            key: 'typeId',
            render: (unknown, row) => row.typeId ? row.type?.name :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: "Филиал",
            key: 'filial',
            render: (unknown, row) => row.filialId ? row.filial?.name :
                <span style={{color: "gray"}}>Не задан</span>
        },
        {
            title: "Сумма",
            key: 'summary',
            render: (unknown, row) => row.summary ? row.summary :
                <span style={{color: "gray"}}>Не задано</span>
        },
        {
            title: "Статус",
            key: 'state',
            render: (unknown, row) => row.state ? row.state?.name :
                <span style={{color: "gray"}}>Не задан</span>
        },
    ]
    const searchFields = [
        {
            title: "#",
            key: "id",
            compareType: "equals",
            operandPosition: 1,
            filterType: "normal"
        },
        {
            title: "Название договора",
            key: "name",
            compareType: "like",
            operandPosition: 1,
            filterType: "normal"
        },
        {
            title: "Номер",
            key: "number",
            compareType: "like",
            operandPosition: 1,
            filterType: "normal"
        },
        {
            title: "БИН",
            key: "client.legalPerson.bin",
            compareType: "like",
            operandPosition: 1,
            filterType: "normal"
        }
    ]

    const filterFields = [
        {
            title: "Cтатус",
            key: "stateId",
            compareType: "equals",
            reducerKey: "contractState",
            reducerArrayKey: "contractStates",
            reducerAction: getContractStates,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Клиент",
            key: "clientId",
            fieldName: "ClientField",
            compareType: "equals",
            operandPosition: 1,
            type: "table",
            filterType: "normal",
        },
        {
            title: "Филиал",
            key: "filialId",
            compareType: "equals",
            reducerKey: "filials",
            reducerArrayKey: "filials",
            reducerAction: getFilials,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Отчетный месяц с",
            key: "actionAt",
            operandPosition: 1,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Отчетный месяц по",
            key: "actionAt",
            operandPosition: 2,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
    ]

    const CRUDTables = {
        standart: CRUDTableStandart,
        query: CRUDTableQuery,
    }

    const CRUDTable = CRUDTables[type]

    return (
        <CRUDTable search={customSearch}
                        columns={columns}
                        {...props}
                        filterFields={filterFields}
                        searchFields={searchFields}
                        rowClassNameHandler={(record) => {
                       if (record?.state?.code === "Signed") {
                           return 'rowSigned'
                       }
                   }}
                        extraFilter={{
                       ...(!isSuperAdmin && {
                           typeId: {
                               operand1: 5,
                               operator: "notEquals"
                           },
                       }),
                       ...(props?.extraFilter && {...props.extraFilter})
                   }}
        />
    );
}
